// Copyright (c) 2026. Hardfin, Inc. All rights reserved.
// Hardfin Confidential - Restricted
import {
  GooglePlacesContext,
  PlacePrediction,
} from '@/contexts/google-places-context';
import { Loader } from '@googlemaps/js-api-loader';
import {
  PropsWithChildren,
  useCallback,
  useEffect,
  useRef,
  useState,
} from 'react';

const loader = new Loader({ apiKey: import.meta.env.VITE_GOOGLE_MAPS_API_KEY });

// Predictions are limited to street addresses and buildings. Leaving out
// `route` matters: an input with a suite, like "2800 Antoine Dr. Suite 2872",
// otherwise fills the list with same-named streets in other cities and drops
// the actual address.
const ADDRESS_PRIMARY_TYPES = ['street_address', 'premise', 'subpremise'];

/**
 * A provider that initializes the Google Places API and provides a
 * GoolePlacesContext to children.
 */
export function GooglePlacesProvider(props: PropsWithChildren) {
  const [isReady, setIsReady] = useState(false);

  const sessionToken = useRef<google.maps.places.AutocompleteSessionToken>();
  const placesService = useRef<google.maps.places.PlacesService>();
  const geocoder = useRef<google.maps.Geocoder>();

  useEffect(() => {
    Promise.all([
      loader.importLibrary('places'),
      loader.importLibrary('geocoding'),
    ]).then(() => {
      setIsReady(true);

      sessionToken.current = new google.maps.places.AutocompleteSessionToken();
    });
  }, []);

  const getPredictions = useCallback(
    async (input: string): Promise<PlacePrediction[]> => {
      if (input === '') {
        return [];
      }

      const request: google.maps.places.AutocompleteRequest = {
        input,
        includedPrimaryTypes: ADDRESS_PRIMARY_TYPES,
        sessionToken: sessionToken.current,
      };

      const { suggestions } =
        await google.maps.places.AutocompleteSuggestion.fetchAutocompleteSuggestions(
          request
        );

      return suggestions.flatMap((suggestion) =>
        suggestion.placePrediction === null
          ? []
          : [
              {
                place_id: suggestion.placePrediction.placeId,
                description: suggestion.placePrediction.text.text,
              },
            ]
      );
    },
    []
  );

  const geocodeAddress = useCallback(
    async (
      address: string,
      componentRestrictions?: google.maps.GeocoderComponentRestrictions
    ): Promise<google.maps.GeocoderResult | null> => {
      if (address === '') {
        return null;
      }

      if (geocoder.current === undefined) {
        geocoder.current = new google.maps.Geocoder();
      }

      const response = await geocoder.current.geocode({
        address,
        componentRestrictions,
      });

      return response.results[0] ?? null;
    },
    []
  );

  const getDetails = useCallback(async (placeId: string) => {
    if (placeId === '') {
      return Promise.reject('placeId cannot be empty');
    }

    if (placesService.current === undefined) {
      placesService.current = new google.maps.places.PlacesService(
        document.createElement('div')
      );
    }

    const request: google.maps.places.PlaceDetailsRequest = {
      placeId,
      sessionToken: sessionToken.current,
      fields: [
        'place_id',
        'geometry',
        'formatted_address',
        'address_components',
      ],
    };

    // According to the documentation from Google, a new session token must be
    // created after querying the details of a place prediction.
    //
    // https://developers.google.com/maps/documentation/javascript/reference/autocomplete-data?hl=en#AutocompleteRequest.sessionToken
    sessionToken.current = new google.maps.places.AutocompleteSessionToken();

    return new Promise<google.maps.places.PlaceResult | null>(
      (resolve, reject) => {
        placesService.current?.getDetails(request, (place, status) => {
          if (status !== 'OK') {
            reject(status);
          }

          resolve(place);
        });
      }
    );
  }, []);

  return (
    <GooglePlacesContext.Provider
      value={{ isReady, getDetails, getPredictions, geocodeAddress }}
    >
      {props.children}
    </GooglePlacesContext.Provider>
  );
}
